import { createFileRoute, Link } from "@tanstack/react-router";
import { NativePushCard } from "@/components/native/NativePushCard";
import { ArrowLeft, Shield, RotateCcw, Heart, Volume2, Sparkles, Eye } from "lucide-react";
import {
  HIDEABLE_TOPICS,
  HIDEABLE_VISUALS,
  COMMON_HIDEABLE_SOUNDS,
  resetComfortPrefs,
  toggleHiddenSound,
  toggleHiddenTopic,
  toggleHiddenVisual,
  useComfortPrefs,
} from "@/lib/preferences";

export const Route = createFileRoute("/preferences")({
  head: () => ({
    meta: [
      { title: "Personal Comfort Settings — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Choose what content appears across Recovery Trauma. Hide sounds, topics, or imagery that don't feel safe for you — no reason needed.",
      },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PreferencesPage,
});

function PreferencesPage() {
  const prefs = useComfortPrefs();

  const reset = () => {
    if (
      typeof window === "undefined" ||
      window.confirm("Restore all hidden content? Nothing will be deleted — everything will simply be visible again.")
    ) {
      resetComfortPrefs();
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
      <Link
        to="/dashboard"
        className="mb-4 inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back
      </Link>

      <header className="mb-6">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-[11px] text-muted-foreground">
          <Shield className="h-3.5 w-3.5" /> Personal Comfort Settings
        </div>
        <h1 className="font-display text-3xl tracking-tight text-foreground sm:text-4xl">
          🛡️ My Safety Preferences
        </h1>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">
          Choose what you'd rather not see. Tap anything to hide it; tap again to bring it back.
          No reason needed — Recovery Trauma adapts to you.
        </p>
      </header>

      <Section
        emoji="🔊"
        icon={Volume2}
        title="Sounds"
        intro="Hidden sounds won't appear in the sounds library, recommendations, or dashboard."
      >
        <ToggleGrid
          items={COMMON_HIDEABLE_SOUNDS.map((s) => ({ id: s.slug, label: s.label }))}
          hidden={prefs.hiddenSounds}
          onToggle={(id) => toggleHiddenSound(id)}
        />
        <p className="mt-3 text-[11px] text-muted-foreground">
          You can also tap "Hide" on any sound card to add it here instantly.
        </p>
      </Section>

      <Section
        emoji="🌿"
        icon={Heart}
        title="Topics"
        intro="Hidden topics are removed from recommendations and featured sections."
      >
        <ToggleGrid
          items={HIDEABLE_TOPICS}
          hidden={prefs.hiddenTopics}
          onToggle={(id) => toggleHiddenTopic(id)}
        />
      </Section>

      <Section
        emoji="👁️"
        icon={Eye}
        title="Visual Preferences"
        intro="We'll do our best to avoid these images and animations across the app."
      >
        <ToggleGrid
          items={HIDEABLE_VISUALS}
          hidden={prefs.hiddenVisuals}
          onToggle={(id) => toggleHiddenVisual(id)}
        />
      </Section>

      <Section
        emoji="✨"
        icon={Sparkles}
        title="Dashboard cards"
        intro="When you tap 'Don't show this again' on any card, it's quietly hidden. They live here."
      >
        {prefs.dismissedCards.length === 0 ? (
          <p className="text-[13px] text-muted-foreground">
            Nothing dismissed yet. Look for the small 🚫 icon on cards across the app.
          </p>
        ) : (
          <p className="text-[13px] text-muted-foreground">
            {prefs.dismissedCards.length} card{prefs.dismissedCards.length === 1 ? "" : "s"} hidden.
            Use the reset button below to bring them all back.
          </p>
        )}
      </Section>

      <div className="mt-8">
        <NativePushCard />
      </div>

      <div className="mt-8 rounded-2xl border border-border/60 bg-card p-5">
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-cream/80"
        >
          <RotateCcw className="h-4 w-4" /> Reset hidden content
        </button>
        <p className="mt-2 text-[11px] text-muted-foreground">
          Restores everything you've hidden. Nothing is deleted — only your visibility preferences change.
        </p>
      </div>

      <p className="mt-8 text-center text-[11px] leading-relaxed text-muted-foreground">
        You never have to explain a trigger. Hide, mute, dismiss — no questions asked.
      </p>
    </div>
  );
}

function Section({
  emoji,
  icon: Icon,
  title,
  intro,
  children,
}: {
  emoji: string;
  icon: typeof Heart;
  title: string;
  intro: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-5 rounded-2xl border border-border/60 bg-card p-5">
      <div className="mb-3 flex items-center gap-2">
        <span aria-hidden className="text-xl">{emoji}</span>
        <h2 className="font-display text-lg text-foreground">{title}</h2>
        <Icon className="ms-auto h-4 w-4 text-muted-foreground" />
      </div>
      <p className="mb-4 text-[12px] leading-relaxed text-muted-foreground">{intro}</p>
      {children}
    </section>
  );
}

function ToggleGrid({
  items,
  hidden,
  onToggle,
}: {
  items: { id: string; label: string }[];
  hidden: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((it) => {
        const isHidden = hidden.includes(it.id);
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onToggle(it.id)}
            aria-pressed={isHidden}
            className={`inline-flex min-h-10 sm:min-h-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12px] font-medium transition ${
              isHidden
                ? "border-[var(--gold-deep)] bg-foreground text-background"
                : "border-border/60 bg-background text-foreground/80 hover:text-foreground"
            }`}
          >
            {isHidden ? "✓ Hidden" : "Hide"} · {it.label}
          </button>
        );
      })}
    </div>
  );
}
